// Registry — runs InsightProviders in parallel with per-provider error isolation.

import type {
  ExternalInsight,
  InsightProvider,
  InsightQuery,
  ProviderError,
} from "./types.js";

export interface RunProvidersResult {
  insights: ExternalInsight[];
  errors: ProviderError[];
}

/**
 * Execute all providers concurrently. Individual failures do not affect others.
 */
export async function runProviders(
  providers: InsightProvider[],
  query: InsightQuery,
): Promise<RunProvidersResult> {
  if (providers.length === 0) {
    return { insights: [], errors: [] };
  }

  const settled = await Promise.allSettled(
    providers.map((p) => p.provide(query)),
  );

  const insights: ExternalInsight[] = [];
  const errors: ProviderError[] = [];

  settled.forEach((result, i) => {
    const provider = providers[i]!;
    if (result.status === "fulfilled") {
      insights.push(result.value);
      return;
    }
    const message =
      result.reason instanceof Error
        ? result.reason.message
        : String(result.reason);
    errors.push({ providerName: provider.name, message });
  });

  return { insights, errors };
}
